import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectActivePromos } from "../../../../store/slices/promoSlice";
import { DeliveryType } from "../../../types/enums";
import { CartItem, PromoInterface, Rule } from "../../../types/types";
import { selectCurrentCluster } from "../../../../store/slices/currentClusterSlice";
import {
  selectHasNonCancelledOrders,
  selectPurchasedCategoryIds,
  selectPurchasedItemIds,
  selectPurchasedStoreIds,
} from "../../../../store/slices/orderSlice";

interface UseCartValidationReturn {
  applicablePromos: PromoInterface[];
  clusterDeliveryRules: Rule[];
  calculateDeliveryCost: (
    itemsTotal: number,
    deliveryType: DeliveryType,
  ) => number;
  calculateDiscount: (
    itemsTotal: number,
    promo?: PromoInterface,
    cartItems?: CartItem[],
    storeId?: number,
    discountCode?: string,
    deliveryType?: DeliveryType,
  ) => number;
  findApplicablePromos: (
    cartItems: CartItem[],
    storeId?: number,
    deliveryType?: DeliveryType,
  ) => PromoInterface[];
  isPromoApplicable: (
    promo: PromoInterface,
    cartItems: CartItem[],
    storeId?: number,
    discountCode?: string,
    deliveryType?: DeliveryType,
  ) => boolean;
  getBestDeliveryRuleText: (itemsTotal: number) => string | null;
  roundMoney: (amount: number) => number;
  calculateItemsTotal: (cartItems: CartItem[]) => number;
  calculateMaxWriteOff: (
    orderTotal: number,
    userBonuses: number,
    writeOffPercent: number,
  ) => number;
  calculateEarnedBonuses: (
    itemsTotal: number,
    bonusPercent: number,
    bonusesToSpend: number,
  ) => number;
  getNextBonusLevelText: (
    userTotalSpent: number,
    bonusRules: Rule[],
  ) => string | null;
}

export const useCartValidation = (): UseCartValidationReturn => {
  const cluster = useSelector(selectCurrentCluster);
  const activePromos = useSelector(selectActivePromos);
  const hasNonCancelledOrders = useSelector(selectHasNonCancelledOrders);

  const purchasedItemIds = useSelector(selectPurchasedItemIds);
  const purchasedCategoryIds = useSelector(selectPurchasedCategoryIds);
  const purchasedStoreIds = useSelector(selectPurchasedStoreIds);

  const purchasedItemIdsMemo = useMemo(
    () => purchasedItemIds,
    [purchasedItemIds],
  );
  const purchasedCategoryIdsMemo = useMemo(
    () => purchasedCategoryIds,
    [purchasedCategoryIds],
  );
  const purchasedStoreIdsMemo = useMemo(
    () => purchasedStoreIds,
    [purchasedStoreIds],
  );

  const clusterDeliveryRules = useMemo(() => {
    const rules = cluster?.delivery || [];
    return [...rules].sort((a, b) => Number(b.level) - Number(a.level));
  }, [cluster?.delivery]);

  const roundMoney = (amount: number): number => {
    return Math.round(amount * 100) / 100;
  };

  const calculateItemsTotal = useCallback((cartItems: CartItem[]): number => {
    let total = 0;
    for (let i = 0; i < cartItems.length; i++) {
      const item = cartItems[i];
      total += item.price * item.quantity;
    }
    return roundMoney(total);
  }, []);

  const isPromoDateValid = useCallback((promo: PromoInterface): boolean => {
    if (!promo.datesImportant) return true;
    const now = new Date();
    const startDate = new Date(promo.startDate);
    const endDate = new Date(promo.endDate);
    return startDate <= now && endDate >= now;
  }, []);

  const findApplicablePromos = useCallback(
    (
      cartItems: CartItem[],
      storeId?: number,
      deliveryType?: DeliveryType,
    ): PromoInterface[] => {
      if (!activePromos.length || !cartItems.length) {
        return [];
      }

      const itemIds = new Set(cartItems.map((item) => item.id));
      const categoryIds = new Set(
        cartItems.map((item) => item.categoryId).filter(Boolean),
      );
      const itemsTotal = calculateItemsTotal(cartItems);

      const applicable = activePromos.filter((promo) => {
        if (!promo.isActive) return false;

        if (!promo.giftBySum) {
          const discountPercent = Number(promo.discount);
          if (isNaN(discountPercent) || discountPercent <= 0) return false;
        }

        if (!isPromoDateValid(promo)) return false;

        if (promo.forPickup) {
          if (deliveryType !== DeliveryType.PICKUP) {
            return false;
          }
          const discountPercent = Number(promo.discount);
          if (isNaN(discountPercent) || discountPercent <= 0) return false;
          if (promo.minSum && itemsTotal < promo.minSum) {
            return false;
          }
        }

        if (promo.giftBySum) {
          if (itemsTotal < (promo.minSum || 0)) {
            return false;
          }
          return true;
        }

        if (promo.forFirstPurchase) {
          if (promo.useItems && promo.itemsFor?.length) {
            const hasPurchasedItem = promo.itemsFor.some((id) =>
              purchasedItemIdsMemo.has(Number(id)),
            );
            if (hasPurchasedItem) return false;
          } else if (promo.useCategories && promo.categoriesFor?.length) {
            const hasPurchasedCategory = promo.categoriesFor.some((id) =>
              purchasedCategoryIdsMemo.has(id),
            );
            if (hasPurchasedCategory) return false;
          } else if (promo.useStores && promo.storesFor?.length) {
            const hasPurchasedStore = promo.storesFor.some((id) =>
              purchasedStoreIdsMemo.has(id),
            );
            if (hasPurchasedStore) return false;
          } else if (hasNonCancelledOrders) {
            return false;
          }
        }

        if (promo.useStores && storeId && promo.storesFor?.length) {
          if (!promo.storesFor.includes(storeId)) return false;
        }

        if (promo.useItems && promo.itemsFor?.length) {
          const hasMatchingItem = promo.itemsFor.some((id) =>
            itemIds.has(String(id)),
          );
          if (!hasMatchingItem) return false;
        } else if (promo.useCategories && promo.categoriesFor?.length) {
          const hasMatchingCategory = promo.categoriesFor.some((catId) =>
            categoryIds.has(catId),
          );
          if (!hasMatchingCategory) return false;
        }

        return true;
      });

      return applicable;
    },
    [
      activePromos,
      isPromoDateValid,
      calculateItemsTotal,
      purchasedItemIdsMemo,
      purchasedCategoryIdsMemo,
      purchasedStoreIdsMemo,
      hasNonCancelledOrders,
    ],
  );

  const isPromoApplicable = useCallback(
    (
      promo: PromoInterface,
      cartItems: CartItem[],
      storeId?: number,
      discountCode?: string,
      deliveryType?: DeliveryType,
    ): boolean => {
      if (!promo.isActive) return false;

      if (!promo.giftBySum) {
        const discountPercent = Number(promo.discount);
        if (isNaN(discountPercent) || discountPercent <= 0) return false;
      }

      if (!isPromoDateValid(promo)) return false;

      if (promo.forPickup) {
        if (deliveryType !== DeliveryType.PICKUP) {
          return false;
        }
        const discountPercent = Number(promo.discount);
        if (isNaN(discountPercent) || discountPercent <= 0) return false;
        if (promo.minSum) {
          const itemsTotal = calculateItemsTotal(cartItems);
          if (itemsTotal < promo.minSum) return false;
        }
      }

      if (promo.giftBySum) {
        const itemsTotal = calculateItemsTotal(cartItems);
        if (itemsTotal < (promo.minSum || 0)) {
          return false;
        }
      }

      if (promo.forFirstPurchase) {
        if (promo.useItems && promo.itemsFor?.length) {
          const hasPurchasedItem = promo.itemsFor.some((id) =>
            purchasedItemIdsMemo.has(Number(id)),
          );
          if (hasPurchasedItem) return false;
        } else if (promo.useCategories && promo.categoriesFor?.length) {
          const hasPurchasedCategory = promo.categoriesFor.some((id) =>
            purchasedCategoryIdsMemo.has(id),
          );
          if (hasPurchasedCategory) return false;
        } else if (promo.useStores && promo.storesFor?.length) {
          const hasPurchasedStore = promo.storesFor.some((id) =>
            purchasedStoreIdsMemo.has(id),
          );
          if (hasPurchasedStore) return false;
        } else if (hasNonCancelledOrders) {
          return false;
        }
      }

      if (promo.useDiscountCode) {
        const enteredCode = discountCode?.trim().toLowerCase();
        const promoCode = promo.discountCode?.trim().toLowerCase();
        if (!enteredCode || enteredCode !== promoCode) {
          return false;
        }
      }

      if (promo.useStores && storeId && promo.storesFor?.length) {
        if (!promo.storesFor.includes(storeId)) {
          return false;
        }
      }

      const itemIds = new Set(cartItems.map((item) => item.id));
      const categoryIds = new Set(
        cartItems.map((item) => item.categoryId).filter(Boolean),
      );

      if (promo.useItems && promo.itemsFor?.length) {
        const hasMatchingItem = promo.itemsFor.some((id) =>
          itemIds.has(String(id)),
        );
        if (!hasMatchingItem) return false;
      } else if (promo.useCategories && promo.categoriesFor?.length) {
        const hasMatchingCategory = promo.categoriesFor.some((id) =>
          categoryIds.has(id),
        );
        if (!hasMatchingCategory) return false;
      }

      const itemsTotal = calculateItemsTotal(cartItems);
      if (itemsTotal <= 0) return false;

      return true;
    },
    [
      isPromoDateValid,
      calculateItemsTotal,
      purchasedItemIdsMemo,
      purchasedCategoryIdsMemo,
      purchasedStoreIdsMemo,
      hasNonCancelledOrders,
    ],
  );

  const calculateDeliveryCost = useCallback(
    (itemsTotal: number, deliveryType: DeliveryType): number => {
      if (deliveryType !== DeliveryType.DELIVERY) {
        return 0;
      }

      if (!clusterDeliveryRules.length) {
        return 0;
      }

      for (let i = 0; i < clusterDeliveryRules.length; i++) {
        if (itemsTotal >= Number(clusterDeliveryRules[i].level)) {
          return roundMoney(Number(clusterDeliveryRules[i].value));
        }
      }

      const lastRule = clusterDeliveryRules[clusterDeliveryRules.length - 1];
      return lastRule ? roundMoney(Number(lastRule.value)) : 0;
    },
    [clusterDeliveryRules],
  );

  const calculateDiscount = useCallback(
    (
      itemsTotal: number,
      promo?: PromoInterface,
      cartItems?: CartItem[],
      storeId?: number,
      discountCode?: string,
      deliveryType?: DeliveryType,
    ): number => {
      if (!promo || !cartItems || cartItems.length === 0) return 0;

      if (promo.giftBySum) return 0;

      if (
        !isPromoApplicable(
          promo,
          cartItems,
          storeId,
          discountCode,
          deliveryType,
        )
      ) {
        return 0;
      }

      const discountPercent = Number(promo.discount);
      const rawDiscount = itemsTotal * (discountPercent / 100);
      const discount = Math.ceil(rawDiscount);

      return discount < itemsTotal ? discount : itemsTotal;
    },
    [isPromoApplicable],
  );

  const getBestDeliveryRuleText = useCallback(
    (itemsTotal: number): string | null => {
      if (clusterDeliveryRules.length < 2) return null;

      const bestRule = clusterDeliveryRules.reduce((prev, current) =>
        Number(prev.value) < Number(current.value) ? prev : current,
      );

      if (itemsTotal >= Number(bestRule.level)) return null;

      const needToAdd = Number(bestRule.level) - itemsTotal;

      if (Number(bestRule.value) === 0) {
        return `🆓 Добавьте на ${needToAdd} ₽ до бесплатной доставки!`;
      } else {
        return `🎯 Добавьте на ${needToAdd} ₽ до минимальной доставки ${bestRule.value} ₽`;
      }
    },
    [clusterDeliveryRules],
  );

  const calculateMaxWriteOff = useCallback(
    (
      orderTotal: number,
      userBonuses: number,
      writeOffPercent: number,
    ): number => {
      if (!orderTotal || !writeOffPercent) return 0;

      const maxByPercent = (orderTotal * writeOffPercent) / 100;
      const maxByBonuses = userBonuses;
      const maxByOrder = orderTotal;

      return Math.floor(Math.min(maxByPercent, maxByBonuses, maxByOrder));
    },
    [],
  );

  const calculateEarnedBonuses = (
    itemsTotal: number,
    bonusPercent: number,
    bonusesToSpend: number = 0,
  ): number => {
    if (!itemsTotal || !bonusPercent) return 0;
    const amountAfterBonus = Math.max(0, itemsTotal - bonusesToSpend);
    const raw = (amountAfterBonus * bonusPercent) / 100;
    return Math.floor(raw);
  };

  const getNextBonusLevelText = useCallback(
    (userTotalSpent: number, bonusRules: Rule[]): string | null => {
      if (!bonusRules?.length) return null;

      const sortedRules = [...bonusRules].sort(
        (a, b) => Number(a.level) - Number(b.level),
      );

      let nextLevel = null;
      for (let i = 0; i < sortedRules.length; i++) {
        if (Number(sortedRules[i].level) > userTotalSpent) {
          nextLevel = sortedRules[i];
          break;
        }
      }

      if (!nextLevel) return null;

      const needToSpend = Number(nextLevel.level) - userTotalSpent;
      return `💰 Еще ${needToSpend} ₽ и будете получать ${nextLevel.value}% бонусами`;
    },
    [],
  );

  const applicablePromos = useMemo(() => [], []);

  return {
    applicablePromos,
    clusterDeliveryRules,
    calculateDeliveryCost,
    calculateDiscount,
    findApplicablePromos,
    isPromoApplicable,
    roundMoney,
    calculateItemsTotal,
    getBestDeliveryRuleText,
    calculateMaxWriteOff,
    calculateEarnedBonuses,
    getNextBonusLevelText,
  };
};
